import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Clock, Plus, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useCreateCall } from "@/hooks/useCalls";
import { useContacts } from "@/hooks/useContacts";
import { useLeads } from "@/hooks/useLeads";
import { toast } from "sonner";

interface ScheduleCallDialogProps {
  selectedDate?: Date;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  preSelectedLeadId?: string;
  preSelectedContactId?: string;
  trigger?: React.ReactNode;
}

export function ScheduleCallDialog({ 
  selectedDate, 
  onSuccess, 
  open: controlledOpen, 
  onOpenChange, 
  preSelectedLeadId,
  preSelectedContactId,
  trigger 
}: ScheduleCallDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange! : setInternalOpen;
  const [date, setDate] = useState<Date | undefined>(selectedDate || new Date());
  const [time, setTime] = useState("10:00");
  const [contactType, setContactType] = useState<"contact" | "lead">(
    preSelectedLeadId ? "lead" : preSelectedContactId ? "contact" : "contact"
  );
  const [selectedContactId, setSelectedContactId] = useState(
    preSelectedLeadId || preSelectedContactId || ""
  );
  const [notes, setNotes] = useState("");
  const [reminder, setReminder] = useState("30");

  const createCall = useCreateCall();
  const { data: contacts } = useContacts();
  const { data: leads } = useLeads();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!date) {
      toast.error("Please select a date");
      return;
    }

    // Combine date and time
    const [hours, minutes] = time.split(":").map(Number);
    const scheduledAt = new Date(date);
    scheduledAt.setHours(hours, minutes, 0, 0);

    createCall.mutate(
      {
        direction: "outbound",
        status: "scheduled",
        scheduled_at: scheduledAt.toISOString(),
        contact_id: contactType === "contact" ? selectedContactId || null : null,
        lead_id: contactType === "lead" ? selectedContactId || null : null,
        notes: notes || undefined,
      },
      {
        onSuccess: () => {
          setOpen(false);
          setDate(selectedDate || new Date());
          setTime("10:00");
          setSelectedContactId("");
          setNotes("");
          onSuccess?.();
          toast.success(`Call scheduled for ${format(scheduledAt, "PPp")}`);
        },
      }
    );
  };

  const timeSlots = [];
  for (let h = 8; h < 20; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, "0");
      const minute = m.toString().padStart(2, "0");
      timeSlots.push(`${hour}:${minute}`);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Schedule Call
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Schedule a Call</DialogTitle>
          <DialogDescription>
            Set up a future call with a contact or lead
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Date Picker */}
            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Picker */}
            <div className="space-y-2">
              <Label>Time</Label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger>
                  <Clock className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot} value={slot}>
                      {slot}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Contact Type */}
          <div className="space-y-2">
            <Label>Contact Type</Label>
            <Select value={contactType} onValueChange={(v) => {
              setContactType(v as "contact" | "lead");
              setSelectedContactId("");
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contact">Contact</SelectItem>
                <SelectItem value="lead">Lead</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Contact/Lead Selection */}
          <div className="space-y-2">
            <Label>{contactType === "contact" ? "Select Contact" : "Select Lead"}</Label>
            <Select value={selectedContactId} onValueChange={setSelectedContactId}>
              <SelectTrigger>
                <SelectValue placeholder={`Choose a ${contactType}`} />
              </SelectTrigger>
              <SelectContent>
                {contactType === "contact"
                  ? contacts?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} {c.email && `(${c.email})`}
                      </SelectItem>
                    ))
                  : leads?.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.first_name} {l.last_name} {l.company_name && `- ${l.company_name}`}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reminder */}
          <div className="space-y-2">
            <Label>Reminder</Label>
            <Select value={reminder} onValueChange={setReminder}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">No reminder</SelectItem>
                <SelectItem value="15">15 minutes before</SelectItem>
                <SelectItem value="30">30 minutes before</SelectItem>
                <SelectItem value="60">1 hour before</SelectItem>
                <SelectItem value="1440">1 day before</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any preparation notes or agenda items..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createCall.isPending}>
              {createCall.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Schedule Call
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
